import React, { useState } from 'react';
import {
  UserOutlined,
  LockOutlined,
  DatabaseOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { Button, Form, Input, Avatar, Typography } from 'antd';
import {
  Container,
  AuthShell,
  BrandPanel,
  LoginPanel,
  ContainerTitle,
  LoginHeader,
  LoginFormWrap,
  SupportList,
} from './styles';
import logo from '../../assets/logo.png';
import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { useI18n } from '../../shared/i18n';

const { Title, Paragraph, Text } = Typography;

interface SignInFormValues {
  clinic: string;
  username: string;
  password: string;
}

const SignIn: React.FC = () => {
  const { signIn } = useAuth();
  const { addToast } = useToast();
  const { t } = useI18n();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const onFinish = async (values: SignInFormValues) => {
    setLoading(true);
    try {
      await signIn(values);
    } catch {
      addToast({
        type: 'error',
        title: t('auth.error.title'),
        description: t('auth.error.description'),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <AuthShell>
        <BrandPanel>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div className="brand-badge">{t('auth.clinicAccess')}</div>
            <LanguageSwitcher compact />
          </div>

          <ContainerTitle>
            <Avatar size={78} src={logo} style={{ background: '#fff' }} />
            <div>
              <Title level={2}>Sigeclin</Title>
              <Paragraph>{t('auth.panelDescription')}</Paragraph>
            </div>
          </ContainerTitle>

          <SupportList>
            <li>
              <strong>{t('auth.support.clinicCodeTitle')}</strong>
              <span>{t('auth.support.clinicCodeDescription')}</span>
            </li>
            <li>
              <strong>{t('auth.support.credentialsTitle')}</strong>
              <span>{t('auth.support.credentialsDescription')}</span>
            </li>
            <li>
              <strong>{t('auth.support.secureAccessTitle')}</strong>
              <span>{t('auth.support.secureAccessDescription')}</span>
            </li>
          </SupportList>
        </BrandPanel>

        <LoginPanel>
          <LoginHeader>
            <div className="icon-box">
              <SafetyCertificateOutlined />
            </div>
            <div>
              <Text className="eyebrow">{t('auth.systemEntry')}</Text>
              <Title level={3}>{t('auth.title')}</Title>
              <Paragraph>{t('auth.subtitle')}</Paragraph>
            </div>
          </LoginHeader>

          <LoginFormWrap>
            <Form name="normal_login" layout="vertical" onFinish={onFinish}>
              <Form.Item
                name="clinic"
                label={t('auth.fields.clinic')}
                extra={t('auth.fields.clinicExtra')}
                rules={[
                  {
                    required: true,
                    message: t('auth.validation.clinicRequired'),
                  },
                ]}
              >
                <Input
                  prefix={<DatabaseOutlined className="site-form-item-icon" />}
                  placeholder={t('auth.fields.clinicPlaceholder')}
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="username"
                label={t('auth.fields.username')}
                rules={[
                  {
                    required: true,
                    message: t('auth.validation.usernameRequired'),
                  },
                ]}
              >
                <Input
                  prefix={<UserOutlined className="site-form-item-icon" />}
                  placeholder={t('auth.fields.usernamePlaceholder')}
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="password"
                label={t('auth.fields.password')}
                rules={[
                  {
                    required: true,
                    message: t('auth.validation.passwordRequired'),
                  },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  name="password"
                  placeholder={t('auth.fields.passwordPlaceholder')}
                  size="large"
                  visibilityToggle={{
                    visible: showPassword,
                    onVisibleChange: setShowPassword,
                  }}
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  className="login-form-button"
                  loading={loading}
                  style={{ width: '100%' }}
                >
                  {t('auth.submit')}
                </Button>
              </Form.Item>
            </Form>
          </LoginFormWrap>
        </LoginPanel>
      </AuthShell>
    </Container>
  );
};

export default SignIn;
